import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * EQ-30: defects across every unit the caller can see. The Home widget, the Open Defects report
 * and the Equipment badge all read one server query (/api/vehicle-defects), so their numbers match.
 */

export type DefectSeverity = 'Low' | 'Medium' | 'High' | 'Critical';
export type DefectStatus = 'Open' | 'Closed';

export const DEFECT_SEVERITIES: DefectSeverity[] = ['Critical', 'High', 'Medium', 'Low'];
export const DEFECT_STATUSES: DefectStatus[] = ['Open', 'Closed'];
export const DEFECT_STATUS_LABEL: Record<DefectStatus, string> = {
  Open: 'Open',
  Closed: 'Closed'
};

export interface DefectServiceArea {
  id: number;
  name: string;
  code: string | null;
}

export interface OpenDefectRow {
  id: number;
  vehicleId: number;
  unitNumber: string;
  unitDescription: string | null;
  serviceArea: DefectServiceArea | null;
  foremanArea: { id: number; name: string } | null;
  category: string | null;
  description: string;
  severity: DefectSeverity;
  status: DefectStatus;
  statusLabel: string;
  reportedAt: string;
  /** YYYY-MM-DD, Pacific. */
  reportedDate: string;
  reportedBy: string | null;
}

export interface CriticalDefectsSummary {
  total: number;
  byServiceArea: Array<{ serviceAreaId: number | null; name: string; code: string | null; count: number }>;
}

export interface OpenDefectFilters {
  severity?: DefectSeverity[];
  status?: DefectStatus[];
  serviceAreaId?: (string | number)[];
  foremanAreaId?: (string | number)[];
  unit?: string;
}

export interface OpenDefectsPage {
  data: OpenDefectRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

function toParams(filters: OpenDefectFilters, extra: Record<string, string | number | undefined> = {}) {
  const params: Record<string, string | number> = {};
  if (filters.severity?.length) params.severity = filters.severity.join(',');
  if (filters.status?.length) params.status = filters.status.join(',');
  if (filters.serviceAreaId?.length) params.serviceAreaId = filters.serviceAreaId.join(',');
  if (filters.foremanAreaId?.length) params.foremanAreaId = filters.foremanAreaId.join(',');
  if (filters.unit?.trim()) params.unit = filters.unit.trim();
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== '') params[key] = value;
  }
  return params;
}

function apiError(error: unknown, fallback: string): Error {
  const data = (error as { response?: { data?: { error?: string } } })?.response?.data;
  return new Error(data?.error || fallback);
}

export const vehicleDefectsService = {
  async criticalSummary(): Promise<CriticalDefectsSummary> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-defects/critical/summary`);
      return response.data;
    } catch (error) {
      throw apiError(error, 'Could not load critical defects.');
    }
  },

  /** The Equipment badge (Manage Repairs only). */
  async criticalCount(): Promise<number> {
    const response = await axios.get(`${BASE_URL}/vehicle-defects/critical/count`);
    return Number(response.data?.count) || 0;
  },

  async listOpen(
    filters: OpenDefectFilters,
    options: { page?: number; limit?: number; sortField?: string; sortDirection?: 'asc' | 'desc' } = {}
  ): Promise<OpenDefectsPage> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-defects/open`, { params: toParams(filters, options) });
      return response.data;
    } catch (error) {
      throw apiError(error, 'Could not load defects.');
    }
  },

  /** Downloads the CSV for the current filters and sort. */
  async exportOpenCsv(
    filters: OpenDefectFilters,
    sort: { sortField?: string; sortDirection?: 'asc' | 'desc' } = {},
    filename = 'Open Defects.csv'
  ): Promise<void> {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-defects/open/export.csv`, {
        params: toParams(filters, sort),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw apiError(error, 'Could not export defects.');
    }
  }
};

export default vehicleDefectsService;
